import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { ModalController } from '@ionic/angular'
import { PlanType } from 'formwork-planner-lib'
import { Subject } from 'rxjs'
import { Plan } from '../../../../../models/plan'
import { Screenshot } from '../../../../../models/screenshot'
import { FileService } from '../../../../../services/file.service'
import { LoadingSpinnerService } from '../../../../../services/loading-spinner.service'
import { ScreenshotService } from '../../../../../services/screenshot.service'
import { Translation } from '../../../../../services/translation.service'
import { PlanSettings } from '../../../../../models/planSettings'
import { PlanSettingsService } from '../../../../../services/plan-settings.service'
import { GeneratePDFService } from '../../../../../services/generate-pdf.service'
import { PlanRepository } from '../../../../../repositories/plan.repository'
import { ModalComponent, ModalDismissAction, ModalStyle } from '@doka-shared/common'
import {
  DynamicFormComponent,
  DynamicInput,
} from '../../../../../shared/components/dynamic-form/dynamic-form.component'
import { AbstractControl, ValidationErrors } from '@angular/forms'

@Component({
  selector: 'efp-pdf-export',
  templateUrl: './pdf-export.component.html',
  styleUrls: ['./pdf-export.component.scss'],
})
export class PdfExportComponent implements OnInit, OnDestroy {
  @Input() screens!: Screenshot[]
  @Input() planId!: number

  public includeTotalStock = true
  public includeCycleStock = true
  private readonly pageLeave$ = new Subject<void>()
  private plan?: Plan
  private planSettings?: PlanSettings
  private buildingType?: PlanType
  public showStockPerCycle = false

  get hasIncludedScreens(): boolean {
    return this.screens.some((screen) => screen.useInPdf)
  }

  constructor(
    private readonly modalCtrl: ModalController,
    private readonly translate: Translation,
    private readonly loadingSpinner: LoadingSpinnerService,
    private readonly fileService: FileService,
    private readonly screenshotService: ScreenshotService,
    private readonly planRepository: PlanRepository,
    private readonly planSettingsService: PlanSettingsService,
    private readonly generatePDFService: GeneratePDFService
  ) {}

  async ngOnInit(): Promise<void> {
    if (this.planId) {
      this.plan = await this.planRepository.findOne(this.planId)
      this.planSettings = await this.planSettingsService.getPlanSettingsAndSetLastUnit(
        this.plan.settingsId
      )
      this.buildingType = this.plan.buildingType
      this.showStockPerCycle = this.plan.buildingType === PlanType.WALL

      if (this.screens.length === 0 && this.planId) {
        this.screens = await this.screenshotService.loadData(this.plan)
        this.screens.forEach((screen) => (screen.useInPdf = true))
      } else {
        this.screens.forEach((screen) => (screen.useInPdf = true))
      }
    }
  }

  ngOnDestroy(): void {
    this.pageLeave$.next()
  }

  async closeModal(): Promise<void> {
    await this.modalCtrl.dismiss()
  }

  public async exportPDF(): Promise<void> {
    if (!this.plan) {
      throw new Error('PdfExportComponent.exportPDF - Plan not found')
    }

    const inputs: DynamicInput[] = [
      {
        type: 'text',
        label: this.translate.translate('PLAN.PROJECT'),
        value: this.plan.name,
        placeholder: this.plan.name,
      },
    ]

    const validationFn = (control: AbstractControl): ValidationErrors => {
      const errors: ValidationErrors = {}
      if (!control.value) errors.emptyInput = this.translate.translate('ERROR.FORMS.EMPTY_INPUT')
      return errors
    }

    const modal = await this.modalCtrl.create({
      component: ModalComponent,
      backdropDismiss: false,
      cssClass: 'small-modal',
      componentProps: {
        dynamicComponent: DynamicFormComponent,
        title: this.translate.translate('SCREENSHOT.SET_NAME'),
        cancelButtonLabel: this.translate.translate('GENERAL.CANCEL'),
        confirmButtonLabel: this.translate.translate('GENERAL.OK'),
        dynamicComponentProps: { options: inputs, validationFn },
      },
    })
    await modal.present()

    const { data, role } = await modal.onDidDismiss()
    if (role === ModalDismissAction.CONFIRM) {
      if (data) {
        const name = data ? data + '.pdf' : 'export.pdf'
        await this.generatePDF(name)
      }
    }
  }

  private async generatePDF(name: string): Promise<void> {
    return this.loadingSpinner.doWithLoadingSpinner(async () => {
      try {
        const screens = this.screens.filter((x) => x.useInPdf)
        if (this.plan && this.planSettings) {
          const pdfResult = await this.generatePDFService.generatePDF(
            this.plan,
            this.planSettings,
            screens,
            this.includeTotalStock,
            this.includeCycleStock,
            ''
          )
          if (pdfResult) {
            await this.fileService.shareOrDownloadBlobFile(pdfResult.pdfBlob, `PDF/${name}`, name)
          }
          await this.closeModal()
        }
      } catch (err: unknown) {
        console.error(err)

        const modal = await this.modalCtrl.create({
          component: ModalComponent,
          backdropDismiss: false,
          cssClass: 'small-modal',
          componentProps: {
            title: this.translate.translate('PDF.ERROR_HEADLINE'),
            text: this.translate.translate('PDF.ERROR_MESSAGE'),
            modalStyle: ModalStyle.Alert,
            cancelButtonLabel: this.translate.translate('GENERAL.CANCEL'),
            confirmButtonLabel: this.translate.translate('GENERAL.OK'),
          },
        })
        await modal.present()

        const { role } = await modal.onDidDismiss()
        if (role === ModalDismissAction.CONFIRM) {
          await this.generatePDF(name)
        }
      }
    })
  }
}
